import type { TFunction } from "i18next";
import type { McpServer } from "@/components/custom/contexts/mcpServersContext";

// Builds the localized `# CONSTRAINT` block that the composer appends to the
// user's message at send-time when one or more selected MCP servers have an
// active date filter. The block is intentionally visible — the user sees what
// was added to their message (the rendered bubble in chat history includes it),
// so they can audit whether the LLM was correctly constrained.
//
// Shape (cs example):
//   <user's typed text>
//
//   # OMEZENÍ
//   Pro zdroj ASPI vyhledávej informace aktuální k datu 19. 5. 2026.
//   Pro zdroj Jogtár vyhledávej informace aktuální k datu 19. 5. 2026.
//
// Returns "" if no constraints to append (caller should skip mutation).
export function buildConstraintAppendix(
  selectedServers: string[],
  allServers: McpServer[],
  dateFilters: Record<string, string>,
  locale: string,
  t: TFunction,
): string {
  const lines: string[] = [];
  for (const name of selectedServers) {
    const server = allServers.find(s => s.name === name);
    if (!server?.dateFilter) continue;
    const iso = dateFilters[name];
    if (!iso) continue;
    const parts = iso.split("-").map(Number);
    if (parts.length !== 3 || parts.some(Number.isNaN)) continue;
    const d = new Date(Date.UTC(parts[0]!, parts[1]! - 1, parts[2]!));
    if (Number.isNaN(d.getTime())) continue;
    let formatted: string;
    try {
      formatted = new Intl.DateTimeFormat(locale, { timeZone: "UTC" }).format(d);
    } catch {
      formatted = iso;
    }
    const label = t(`${name}.label`);
    lines.push(t("composer.mcp.constraint.line", { server: label, date: formatted }));
  }
  if (lines.length === 0) return "";
  return `\n\n${t("composer.mcp.constraint.header")}\n${lines.join("\n")}`;
}
